/** 成功案例：列表与详情共用，封面图为题材相近的免版权配图，非客户现场实拍。 */

export type SuccessCaseCategory =
  | "logistics"
  | "ecommerce"
  | "manufacturing"
  | "government"
  | "retail"
  | "hospitality"
  | "erp"
  | "sports"
  | "healthcare";

export interface SuccessCase {
  id: number;
  title: string;
  description: string;
  content: string;
  image: string;
  date: string;
  category: SuccessCaseCategory;
  views: number;
  tags: string[];
}

export const SUCCESS_CASES_LIST_PATH = "/success-cases";

export const SUCCESS_CASE_CATEGORY_OPTIONS: {
  label: string;
  value: "all" | SuccessCaseCategory;
}[] = [
  { label: "全部", value: "all" },
  { label: "物流与供应链", value: "logistics" },
  { label: "电商与平台", value: "ecommerce" },
  { label: "智能制造", value: "manufacturing" },
  { label: "政务与教育", value: "government" },
  { label: "商业地产", value: "retail" },
  { label: "酒店文旅", value: "hospitality" },
  { label: "园区ERP", value: "erp" },
  { label: "体育休闲", value: "sports" },
  { label: "医疗健康", value: "healthcare" },
];

export function getSuccessCaseCategoryLabel(
  value: SuccessCaseCategory
): string {
  const hit = SUCCESS_CASE_CATEGORY_OPTIONS.find((o) => o.value === value);
  return hit?.label ?? value;
}

export const successCases: SuccessCase[] = [
  {
    id: 1,
    title: "中国邮政合作：EMS 包裹全链路管理系统",
    description:
      "为邮政系物流场景定制揽收、分拣、在途与客户查询的一体化系统，支撑高峰件量与多级网点协同。",
    image: "/images/cases/case-1.jpg",
    date: "2026-01-26",
    category: "logistics",
    views: 1820,
    tags: ["物流", "EMS", "工单与轨迹", "高并发"],
    content: `
<p>本项目聚焦邮政类快递在高并发揽收与多级中转场景下的<code>可追溯</code>与<code> SLA 管控</code>需求，
在不大改既有作业习惯的前提下，用一套统一的包裹管理系统串联网点、分拨与用户侧查询。</p>
<h2>建设背景</h2>
<ul>
<li>业务峰值明显，分拣与出库环节易出现滞留与信息不对称。</li>
<li>多端（网点 / 呼叫中心 / C 端查询）数据来源分散，复盘成本高。</li>
</ul>
<h2>方案概述</h2>
<ul>
<li><strong>全链路跟踪</strong>：单号生命周期从揽收到签收节点可配置化，异常件自动告警。</li>
<li><strong>网点与班次</strong>：排班、交接班与签收稽核电子化，减轻纸质与手工对账。</li>
<li><strong>对外查询</strong>：限频与风控策略兼顾体验，降低接口被滥用风险。</li>
</ul>
<h2>交付价值</h2>
<p>系统上线后，内部协同与客服定位问题的耗时显著缩短，峰值期的滞留预警与可追溯能力得到提升，为后续与更多区域网点推广打下基础。</p>
`,
  },
  {
    id: 2,
    title: "拼多多合作：供应商全生命周期管理系统",
    description:
      "面向平台供货商入驻、履约、结算与评价的数字化管理套件，适配多 SKU、多批次与多级审核场景。",
    image: "/images/cases/case-2.jpg",
    date: "2025-11-08",
    category: "ecommerce",
    views: 2145,
    tags: ["电商平台", "供应商", "履约", "结算"],
    content: `
<p>在平台型电商体系中，供货商侧的流程标准化程度直接影响履约效率与资金安全。本系统串联
<strong>准入</strong>、<strong>商品与批次</strong>、<strong>订单协同</strong>与<strong>对账结算</strong>等关键环节，
减少跨部门反复沟通。</p>
<h2>核心业务模块</h2>
<ul>
<li>供应商画像与分层：资质、品类、历史绩效一目了然。</li>
<li>履约与 SLA：出库、发货、售后节点可模板化配置。</li>
<li>账单与核销：支持与内部财务规则对齐的差异处理与留痕。</li>
</ul>
<h2>效果摘要</h2>
<p>管理动作从分散表格收拢至统一工作台，抽检与复盘可基于单据与时间线一键展开，显著提升运营与风控同事的协作效率。</p>
`,
  },
  {
    id: 3,
    title: "富士康郑州科技园：内部即时沟通与协同平台",
    description:
      "面向大型制造园区内多班组、多供应商驻场的安全可控即时通讯与管理协同能力，支持与现有账号体系打通。",
    image: "/images/cases/case-3.jpg",
    date: "2025-07-19",
    category: "manufacturing",
    views: 1688,
    tags: ["智能制造", "IM", "权限", "园区协同"],
    content: `
<p>大型电子制造园区对<strong>信息安全</strong>、<strong>组织边界</strong>与<strong>消息留存审计</strong>要求极高，
传统公共即时通讯难以满足合规与本地化部署诉求。</p>
<h2>设计要点</h2>
<ul>
<li>租户与组织架构按园区 / 楼栋 / 产线多级建模，消息与文件的可见范围可精细配置。</li>
<li>群组与工单机器人联动：产线告警、巡检记录可一键推送到责任人群。</li>
<li>离线弱网环境下的消息投递策略与端到端链路监控。</li>
</ul>
<h2>客户收益</h2>
<p>重要通知到达率与时效性明显提升，一线与后勤、驻场供应商的沟通成本下降，同时为审计与事故追溯提供可查记录。</p>
`,
  },
  {
    id: 4,
    title: "安阳市教育部门合作：中小学在线考试与考务平台",
    description:
      "覆盖命题、组卷、在线监考辅助、成绩分析与补考全流程，兼顾公平性与教体局统计分析需求。",
    image: "/images/cases/case-4.jpg",
    date: "2025-03-14",
    category: "government",
    views: 1456,
    tags: ["在线教育", "考试", "阅卷", "数据看板"],
    content: `
<p>区域性大规模在线考试需在<strong>稳定可靠</strong>与<strong>防作弊辅助</strong>之间取得平衡，并适配不同学校网络条件。</p>
<h2>能力范围</h2>
<ul>
<li>题库与知识点标签：支持教体局统一管理或学校分级维护两种模式。</li>
<li>考试编排：错峰开考、监考教师分工与异常行为预警提示。</li>
<li>分析与报表：按区县、年级、学科输出对比与薄弱环节分析。</li>
</ul>
<h2>落地说明</h2>
<p>通过分阶段试运行与题库审核机制，考务组织从“多表格”过渡到“一单到底”，教体部门掌握全区进度与大屏指标的实时性显著提升。</p>
`,
  },
  {
    id: 5,
    title: "济南贵和购物中心：场内运营与租户协同管理系统",
    description:
      "整合商户合同、活动策划、巡检与工单，服务购物中心日常运营的一体化控制台。",
    image: "/images/cases/case-5.jpg",
    date: "2024-11-27",
    category: "retail",
    views: 1120,
    tags: ["商业地产", "运营", "工单", "商户"],
    content: `
<p>大型商场运营涉及<strong>多租户协同</strong>、<strong>临时活动报备</strong>与<strong>工程物业工单</strong>，
若仅靠邮件与群组，易产生责任不清与无法量化的问题。</p>
<h2>系统亮点</h2>
<ul>
<li>租户门户：营销活动、装修时程与营业时间变更可结构化提交与审批。</li>
<li>现场巡检：巡检路线、拍照与水印时间绑定，自动生成整改单。</li>
<li>数据中心：客流量与活动报备与销售额线索做轻量化关联视图（不含具体商业机密算法）。</li>
</ul>
<h2>小结</h2>
<p>运营与物业团队在统一看板上协作，问题整改闭环可追踪，为商场方与租户之间的信息不对称提供了数字化缓冲。</p>
`,
  },
  {
    id: 6,
    title: "扬州市城市酒店：一体化内部酒店管理后台",
    description:
      "房态房价、客源渠道、能耗与 housekeeping 工单一体化，适配多门店扩展。",
    image: "/images/cases/case-6.jpg",
    date: "2024-06-09",
    category: "hospitality",
    views: 980,
    tags: ["酒店PMS扩展", "房态", "能耗", "多店"],
    content: `
<p>城市型酒店在非连锁标准系统之外，往往需要<strong>更贴合本地管理习惯</strong>的内部扩展模块，
本项目在保留核心房态枢纽的前提下补强运营侧能力。</p>
<h2>主要功能</h2>
<ul>
<li>房态与房价策略：淡季、长租与团体协议价规则可配置。</li>
<li>内部工单：保洁、维修与查房任务移动端闭环。</li>
<li>能耗与成本：分项采集与周报，辅助店长做节能决策。</li>
</ul>
<h2>成果</h2>
<p>店总与前台、工程之间的信息同步更顺畅，部分重复性问询与手写交接被系统替代。</p>
`,
  },
  {
    id: 7,
    title: "福州钢铁物流园：园区内 ERP 与仓储协同系统",
    description:
      "围绕钢材进出库、运输计划与园区计费定制 ERP，打通磅房、堆场与客户结算核心流程。",
    image: "/images/cases/case-7.jpg",
    date: "2024-01-17",
    category: "erp",
    views: 1325,
    tags: ["钢铁物流", "ERP", "磅房", "仓储"],
    content: `
<p>钢铁物流园业务具有<strong>货值高</strong>、<strong>计量环节多</strong>、<strong>结算周期长</strong>等特点，
通用的轻量进销存往往难以贴合现场。</p>
<h2>关键环节</h2>
<ul>
<li>车辆与泊位调度：超重与危化车型提示，减少现场拥堵。</li>
<li>称重与复检：称重数据与单据影像绑定防篡改链路。</li>
<li>计费与账单：吊装、仓储、装卸费项可组装为合同模板。</li>
</ul>
<h2>价值陈述</h2>
<p>财务与库区对同一批货物的理解趋于一致，对账异议减少，园区的周转可视化程度提升。</p>
`,
  },
  {
    id: 8,
    title: "香港九木高尔夫球场：场内运营综合管理后台",
    description:
      "会员预订、场次排班、球童与教具调度、会费与活动等模块一体化，适配高端球场日常运营节奏。",
    image: "/images/cases/case-8.jpg",
    date: "2023-08-25",
    category: "sports",
    views: 860,
    tags: ["高尔夫", "会员", "预订", "运营管理"],
    content: `
<p>高尔夫球场运营强调<strong>体验</strong>与<strong>档期精确</strong>，人工协调球童与出发台在高旺季压力较大，
系统目标是在不打断击球节奏的前提下提高内部调度效率。</p>
<h2>模块说明</h2>
<ul>
<li>预订与锁场：多渠道冲突检测，支持候补与改签策略。</li>
<li>教具与耗材：出库损耗与维保记录。</li>
<li>会员与积分：活动和权益包可模板化上架。</li>
</ul>
<h2>综述</h2>
<p>运营侧对当日容量与收入的把握更前瞻，贵宾礼宾团队的前台问询压力有所缓解。</p>
`,
  },
  {
    id: 9,
    title: "广西桂林临城医院：院内信息化综合管理支撑系统",
    description:
      "面向行政、资产、后勤工单与库房申领等场景的一体化院内系统（非临床诊疗核心业务替代），侧重流程可追溯与对内效率。",
    image: "/images/cases/case-9.jpg",
    date: "2023-03-18",
    category: "healthcare",
    views: 1540,
    tags: ["医院信息化", "后勤", "资产", "工单"],
    content: `
<p>医院的<strong>后勤保障</strong>与<strong>资产耗材管理</strong>若仍大量依赖纸质与电话，容易出现响应慢与责任链路不清。
本系统在明确边界（不替代 HIS 诊疗核心）前提下，为政工后勤提供统一入口。</p>
<h2>建设范围（示例）</h2>
<ul>
<li>工单与维保：医疗设备报修与普通后勤报修的分类 SLA。</li>
<li>库房与申领：科室常用耗材阈值预警与分批配送。</li>
<li>资产管理：条码 / 台账与盘点移动端。</li>
</ul>
<h2>结语</h2>
<p>通过流程节点留痕与统计报表，院务管理团队对响应时效与积压情况更加心中有数，为非临床侧的精细化运营提供工具支撑。</p>
`,
  },
];

export function getSuccessCaseById(id: number): SuccessCase | undefined {
  return successCases.find((c) => c.id === id);
}
